import { useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi } from "@/lib/auth";
import { errorText } from "@/lib/errors";

const input = "mt-1 block w-full bg-transparent border-b border-pine/40 py-2 text-base focus:outline-none focus:border-brass transition-colors";
const BLANK = { title: "", start_date: "", end_date: "", location: "", description: "", seats_total: "20", status: "open" };

const DateField = ({ label, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
  <div className="text-sm">
    {label}
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="mt-1 flex w-full items-center gap-2 border-b border-pine/40 py-2 text-left text-base" data-testid={testId}>
          <CalendarDays size={16} className="text-brass" />
          {value ? format(parseISO(value), "MMM d, yyyy") : <span className="text-moss">Pick a date</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-paper" align="start">
        <Calendar mode="single" selected={value ? parseISO(value) : undefined} onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  </div>
  );
};

export const RetreatForm = ({ open, onOpenChange, retreat, onSaved }) => {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [initFor, setInitFor] = useState(null);
  const key = retreat?.id || "new";
  if (open && initFor !== key) {
    setInitFor(key);
    setForm(retreat ? { ...retreat, description: retreat.description || "", seats_total: String(retreat.seats_total) } : BLANK);
  }
  if (!open && initFor) setInitFor(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error("Please pick the start and end dates.");
    setSaving(true);
    const body = { title: form.title, start_date: form.start_date, end_date: form.end_date, location: form.location,
      description: form.description || null, seats_total: Number(form.seats_total), status: form.status };
    try {
      if (retreat) await adminApi.put(`/admin/retreats/${retreat.id}`, body);
      else await adminApi.post("/admin/retreats", body);
      toast.success(retreat ? "Retreat updated." : "Retreat added. It now shows on your website.");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="site bg-paper text-pine border-none rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="retreat-form-dialog">
        <DialogHeader><DialogTitle className="dp text-3xl font-normal">{retreat ? "Edit retreat" : "Add a retreat"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4" data-testid="retreat-form">
          <label className="text-sm">Title<input required value={form.title} onChange={set("title")} className={input} data-testid="retreat-title-input" /></label>
          <div className="grid grid-cols-2 gap-4">
            <DateField label="Starts" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="retreat-start-date" />
            <DateField label="Ends" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="retreat-end-date" />
          </div>
          <label className="text-sm">Location<input required value={form.location} onChange={set("location")} className={input} data-testid="retreat-location-input" /></label>
          <label className="text-sm">Description (optional)<textarea rows="3" value={form.description} onChange={set("description")} className={input} data-testid="retreat-description-input" /></label>
          <div className="grid grid-cols-2 gap-4">
            <label className="text-sm">Total seats<input required type="number" min="1" max="500" value={form.seats_total} onChange={set("seats_total")} className={input} data-testid="retreat-seats-input" /></label>
            <div className="text-sm">
              Status
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-pine/40" data-testid="retreat-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open" data-testid="retreat-status-open">Open for reservations</SelectItem>
                  <SelectItem value="closed" data-testid="retreat-status-closed">Hidden from website</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <button type="submit" disabled={saving} className="mt-2 rounded-full bg-pine text-sage py-4 disabled:opacity-70" data-testid="retreat-save-button">{saving ? "Saving..." : "Save retreat"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
